import { LegalPage } from './LegalPage';

const CONTACT_EMAIL = 'support@tenkventures.com';
const UPDATED = 'May 14, 2026';

export function PrivacyScreen() {
  return (
    <LegalPage title="Privacy Policy" updatedDate={UPDATED}>
      <p>
        Lumé ("we", "us") is operated by Ten Ventures. This Privacy Policy explains what we
        collect when you use lume.skin, why we collect it, and what your rights are.
      </p>
      <p>
        We've kept this short and plain because we collect very little: enough to give you
        your skin analysis and deliver the plan you paid for.
      </p>

      <h2>1. What we collect</h2>

      <h3>From you, directly</h3>
      <ul>
        <li>
          <strong>A selfie photo.</strong> You take it (or upload it) during the scan flow.
        </li>
        <li>
          <strong>Your real age.</strong> Used to calibrate the skin-age comparison.
        </li>
        <li>
          <strong>Skincare goals.</strong> Used to prioritise your plan.
        </li>
        <li>
          <strong>Your email address.</strong> Collected at the paywall, used to deliver your
          plan link and (later) optional product updates.
        </li>
      </ul>

      <h3>From your payment</h3>
      <ul>
        <li>
          <strong>Stripe handles the card.</strong> We never see, touch, or store your full
          card number. Stripe gives us back a confirmation token, your name, and your email.
        </li>
      </ul>

      <h3>Technical</h3>
      <ul>
        <li>
          Basic request logs (IP, user agent, timing) at the edge level via Cloudflare. We
          do not use third-party advertising cookies. We may add privacy-friendly aggregate
          analytics (e.g., Cloudflare Web Analytics) which do not use cookies and do not
          track individuals across sites.
        </li>
      </ul>

      <h2>2. How we use it</h2>
      <ul>
        <li>
          <strong>To analyse your skin.</strong> Your photo is sent to our skin analysis
          provider (Perfect Corp) to compute scores like wrinkles, pigmentation, hydration,
          etc. Perfect Corp does not retain images for marketing or model training and
          processes them solely to return scores to us.
        </li>
        <li>
          <strong>To build your routine.</strong> Your skin scores (not your photo) plus your
          age and goals are sent to our AI model provider (Anthropic) to generate the
          recommended routine text.
        </li>
        <li>
          <strong>To deliver your plan.</strong> The generated plan is stored against a
          random token, accessible at a unique URL we share with you and (optionally) email
          you.
        </li>
        <li>
          <strong>To process your payment.</strong> Stripe handles this. We rely on Stripe's
          fraud + security protections.
        </li>
      </ul>

      <h2>3. Face photos (biometric data)</h2>
      <p>
        Photos of faces can be considered <strong>biometric data</strong> under some laws
        (Illinois BIPA, Texas CUBI, EU GDPR, and others). By using Lumé you explicitly
        consent to:
      </p>
      <ul>
        <li>
          The collection of your face photo for the purpose of skin analysis;
        </li>
        <li>
          Its transmission to Perfect Corp (our analysis provider) for the purpose of
          generating skin scores;
        </li>
        <li>
          Storage of the photo associated with your purchased plan (so you can revisit
          before/after comparisons) for up to 90 days, after which it is deleted.
        </li>
      </ul>
      <p>
        You can request immediate deletion of your photo at any time by emailing{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>

      <h2>4. Who we share with</h2>
      <p>We share only what's necessary, with these processors:</p>
      <ul>
        <li>
          <strong>Cloudflare</strong> — hosting, edge proxying, KV storage of your plan.
        </li>
        <li>
          <strong>Perfect Corp</strong> — skin analysis (photo + scores).
        </li>
        <li>
          <strong>Anthropic</strong> — generates your routine text from your skin scores
          (no photo sent).
        </li>
        <li>
          <strong>Stripe</strong> — processes your payment.
        </li>
        <li>
          <strong>Resend</strong> (if email delivery is enabled) — sends your plan-link
          email.
        </li>
      </ul>
      <p>We do not sell your data to anyone. Ever.</p>

      <h2>5. Retention</h2>
      <ul>
        <li><strong>Your plan + scores:</strong> 90 days, then auto-deleted.</li>
        <li>
          <strong>Your photo:</strong> 90 days (so before/after comparisons work), then
          auto-deleted.
        </li>
        <li>
          <strong>Email + payment record:</strong> retained for tax and accounting purposes
          as long as legally required (typically 7 years).
        </li>
      </ul>

      <h2>6. Your rights</h2>
      <p>
        You have the right to access, correct, or delete the personal data we hold about
        you. To exercise any of these rights, email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We respond within 30 days.
      </p>
      <p>
        If you are in the EU/UK, you have additional rights under GDPR including data
        portability and the right to lodge a complaint with your local data protection
        authority. If you are in California, you have rights under CCPA/CPRA.
      </p>

      <h2>7. Children</h2>
      <p>
        Lumé is not intended for use by anyone under 13. The scan flow requires age
        confirmation. If we learn we have collected data from a child under 13, we delete it
        immediately.
      </p>

      <h2>8. Security</h2>
      <p>
        All data is transmitted over HTTPS. Card data is handled exclusively by Stripe (PCI
        DSS Level 1 certified). Plan tokens are 128-bit random values — effectively
        unguessable.
      </p>

      <h2>9. Changes</h2>
      <p>
        If we make material changes to this policy, we'll post a notice on this page and
        update the "Last updated" date. Continued use after changes means you accept the
        revised policy.
      </p>

      <h2>10. Contact</h2>
      <p>
        Questions, deletion requests, or anything else:{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </LegalPage>
  );
}
